import React, { useEffect, useRef, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Menu, Bell, Search, User, LogOut, ChevronDown } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { toast } from 'react-toastify';
import { auth } from '../../services/firebase';
import { ConfirmDialog } from '../ui';

interface NavbarProps {
  onMenuClick: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onMenuClick }) => {
  const [user] = useAuthState(auth);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Error logging out');
    }
  };

  return (
    <header className="bg-[#17140f] border-b border-[#3a3122] px-4 lg:px-6 h-[73px] flex items-center">
      <div className="flex items-center justify-between w-full">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden text-[#8a7f6e] hover:text-[#b9ad9b] p-1"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Search */}
          <div className="hidden md:flex relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#8a7f6e]" />
            <input
              type="text"
              placeholder="Search students, teachers..."
              className="pl-10 pr-4 py-2 w-80 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#332217] focus:border-[#e2916d] bg-[#211c15] text-[#f3ede2] placeholder-[#8a7f6e] text-sm"
            />
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <button className="relative text-[#8a7f6e] hover:text-[#b9ad9b] p-2">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-[#f38b83] rounded-full"></span>
          </button>

          {/* User profile */}
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => setProfileMenuOpen(prev => !prev)}
              className="flex items-center space-x-3 hover:bg-[#211c15] rounded-lg px-2 py-1.5 transition-colors"
            >
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-[#f3ede2]">
                  {user?.displayName || 'Administrator'}
                </p>
                <p className="text-xs text-[#b9ad9b]">{user?.email}</p>
              </div>
              <div className="w-8 h-8 bg-[#e2916d] rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-[#241408]" />
              </div>
              <ChevronDown className={`hidden sm:block w-4 h-4 text-[#8a7f6e] transition-transform duration-200 ${profileMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {profileMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-[#211c15] border border-[#3a3122] rounded-lg shadow-lg z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-[#3a3122] sm:hidden">
                  <p className="text-sm font-medium text-[#f3ede2]">{user?.displayName || 'Administrator'}</p>
                  <p className="text-xs text-[#b9ad9b] truncate">{user?.email}</p>
                </div>
                <button
                  onClick={() => {
                    setProfileMenuOpen(false);
                    setConfirmLogoutOpen(true);
                  }}
                  className="w-full flex items-center space-x-2 px-4 py-3 text-sm font-medium text-[#f38b83] hover:bg-[#341a17] transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmLogoutOpen}
        onClose={() => setConfirmLogoutOpen(false)}
        onConfirm={handleLogout}
        title="Sign Out"
        message="Are you sure you want to sign out?"
        type="danger"
        confirmText="Yes, Sign Out"
        cancelText="Cancel"
      />
    </header>
  );
};

export default Navbar;
